import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  Input,
  List,
  Spin,
  Typography,
  Space,
  Timeline,
  Select,
  Row,
  Col,
  Divider,
  message,
  Grid,
} from 'antd';
import {
  SearchOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { fetchCustomers, updateCustomerAadhaar } from '../api';
import { ActionType, ModuleType, PermissionButton } from "components";
import { motion, AnimatePresence } from 'framer-motion';
import "bootstrap/dist/css/bootstrap.min.css";

const { Text, Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const BILLING_STEPS = Object.freeze({
  SEARCH_CUSTOMER: { label: 'Search Customer', value: 0 },
  SELECT_SCHEME: { label: 'Select Scheme', value: 1 },
});

const STEP_SEQUENCE = [
  BILLING_STEPS.SEARCH_CUSTOMER,
  BILLING_STEPS.SELECT_SCHEME,
];

const CreateBillPopup = ({ schemesList = [] }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(BILLING_STEPS.SEARCH_CUSTOMER.value);
  const [loading, setLoading] = useState(false);
  const [transitionLoading, setTransitionLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);

  const [aadhaarInput, setAadhaarInput] = useState('');
  const [selectedScheme, setSelectedScheme] = useState('');
  const [agentCode, setAgentCode] = useState('');

  const navigate = useNavigate();
  const module = ModuleType.BILLING;
  const screens = useBreakpoint();
  const [localSchemes, setLocalSchemes] = useState([]);

  const selectedSchemeObject = localSchemes.find(
    (s) => s._id === selectedScheme
  );

  useEffect(() => {
    let newSchemes = [];
    if (Array.isArray(schemesList)) {
      newSchemes = schemesList;
    } else if (
      schemesList &&
      typeof schemesList === "object" &&
      Array.isArray(schemesList.data?.data)
    ) {
      newSchemes = schemesList.data.data;
    } else {
      if (localSchemes.length !== 0) {
        setLocalSchemes([]);
      }
      return;
    }
    // Only update if different (shallow check)
    if (
      newSchemes.length !== localSchemes.length ||
      newSchemes.some((s, i) => s._id !== localSchemes[i]?._id)
    ) {
      setLocalSchemes(newSchemes);
    }
  }, [schemesList]); // do not add localSchemes to deps



  const resetAll = () => {
    setStep(BILLING_STEPS.SEARCH_CUSTOMER.value);
    setSearchTerm('');
    setCustomers([]);
    setCustomer(null);
    setAadhaarInput('');
    setSelectedScheme('');
    setAgentCode('');
    setLoading(false);
    setTransitionLoading(false);
  };

  const handleOpen = () => {
    resetAll();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetAll();
  };

  const isMoreThanOneDayOld = (createdAt) => {
    if (!createdAt) return true;
    const created = new Date(createdAt);
    const now = new Date();
    const diffInMs = now - created;
    return diffInMs > 24 * 60 * 60 * 1000;
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return message.warning('Please enter a search term');
    setLoading(true);
    try {
      const res = await fetchCustomers({ search: searchTerm });
      setCustomers(res?.data || []);
    } catch {
      message.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (cust) => {
    setCustomer(cust);
    setStep(BILLING_STEPS.SELECT_SCHEME.value);
  };

  const handleUpdateAadhaar = async () => {
    if (!aadhaarInput.trim()) return message.warning('Enter Aadhaar');
    setTransitionLoading(true);
    try {
      await updateCustomerAadhaar(customer._id, aadhaarInput);
      message.success('Aadhaar updated');
      setStep(BILLING_STEPS.SELECT_SCHEME.value);
    } catch {
      message.error('Update failed');
    } finally {
      setTransitionLoading(false);
    }
  };

  const handleProceedAccrued = () => {
    if (!selectedScheme) {
      console.warn("⚠️ No scheme selected");
      return message.warning("Select a scheme");
    }

    const schemeDetails = localSchemes.find((s) => s._id === selectedScheme);
    console.log("Found schemeDetails:", schemeDetails);

    if (schemeDetails.type === "Agent" && !agentCode.trim()) {
      return message.warning("Enter agent code");
    }


    // Consolidated data
    const consolidated = {
      customer,
      aadhaar: aadhaarInput || customer?.aadhaar,
      scheme: schemeDetails,
      agentCode: schemeDetails.type === "Agent" ? agentCode : undefined,
    };

    navigate('/billing/create', { state: { consolidated } });
    handleClose();
  };

  const handleBack = () =>
    setStep((prev) => Math.max(BILLING_STEPS.SEARCH_CUSTOMER.value, prev - 1));
  const contentVariants = {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -40 },
  };

  return (
    <>
      <PermissionButton
        type="primary"
        buttonType={ActionType.CREATE}
        module={module}
        onClick={handleOpen}
        className="mb-2"
      >
        Create Bill
      </PermissionButton>

      <Modal
        open={open}
        onCancel={handleClose}
        footer={null}
        width={screens.xs ? "98vw" : "90%"}
        style={{ maxWidth: 1000 }}
        bodyStyle={{ padding: screens.xs ? 12 : 24, paddingBottom: 80 }}
        centered
        aria-modal="true"
      >
        <Title level={4} style={{ textAlign: 'center', marginBottom: 30 }}>
          Create Bill
        </Title>

        <Row gutter={[screens.xs ? 0 : 32, 0]}>
          {/* Timeline */}
          <Col xs={24} sm={6} className="mb-3">
            <Timeline mode="left">
              {STEP_SEQUENCE.map(({ label, value }) => (
                <Timeline.Item
                  key={label}
                  color={step >= value ? 'blue' : 'gray'}
                  dot={step === value ? <ClockCircleOutlined /> : null}
                >
                  <Text strong={step >= value}>{label}</Text>
                </Timeline.Item>
              ))}
            </Timeline>
          </Col>

          {/* Step Content */}
          <Col xs={24} sm={18}>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={contentVariants}
                transition={{ duration: 0.3 }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {step === BILLING_STEPS.SEARCH_CUSTOMER.value && (
                    <>
                      <Row gutter={8} className="mb-2">
                        <Col flex="auto">
                          <Input
                            placeholder="Search by Aadhaar, PAN, or Phone"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onPressEnter={handleSearch}
                            aria-label="Search Customer"
                          />
                        </Col>
                        <Col>
                          <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            loading={loading}
                            aria-label="Search"
                          />
                        </Col>
                      </Row>

                      {customers.length > 0 && (
                        <List
                          bordered
                          dataSource={customers}
                          renderItem={(cust) => (
                            <List.Item
                              key={cust._id}
                              onClick={() => handleSelect(cust)}
                              style={{ cursor: 'pointer' }}
                              aria-label={`Select customer ${cust.name}`}
                            >
                              <Text strong>{cust.name} ({cust.phone})</Text><br />
                              <Text type="secondary">Aadhaar: {cust.aadhaar || 'N/A'}</Text>
                            </List.Item>
                          )}
                          style={{ maxHeight: 220, overflowY: 'auto' }}
                        />
                      )}
                      {loading && <div className="text-center"><Spin /></div>}
                    </>
                  )}


                  {step === BILLING_STEPS.SELECT_SCHEME.value && (
                    <>
                      <Row gutter={16} className="mb-2">
                        <Col flex="auto">
                          <Select
                            placeholder="Select Scheme"
                            value={selectedScheme}
                            onChange={(val) => {
                              setSelectedScheme(val);
                              setAgentCode('');
                            }}
                            style={{ width: '100%' }}
                            aria-label="Select Scheme"
                          >
                            {localSchemes.map((scheme) => (
                              <Option key={scheme._id} value={scheme._id}>
                                {scheme.name} ({scheme.type})
                              </Option>
                            ))}
                          </Select>
                        </Col>
                        <Col>
                          {selectedSchemeObject && (
                            <Text type="secondary">
                              ₹{selectedSchemeObject.ratePerGram || "N/A"}/g
                            </Text>
                          )}
                        </Col>
                      </Row>

                      {/* Agent Code Input */}
                      {selectedSchemeObject?.type === "Agent" && (
                        <Row style={{ marginTop: 16 }}>
                          <Col>
                            <Input
                              placeholder="Enter Agent Code"
                              value={agentCode}
                              onChange={(e) => setAgentCode(e.target.value)}
                              style={{ maxWidth: 300 }}
                            />
                          </Col>
                        </Row>
                      )}
                    </>
                  )}

                  <div style={{ height: 64 }} /> {/* Spacer for footer */}
                </Space>
              </motion.div>
            </AnimatePresence>
          </Col>
        </Row>

        {/* Fixed Footer */}
        <div
          style={{
            position: 'absolute',
            bottom: 16,
            left: screens.xs ? 8 : 24,
            right: screens.xs ? 8 : 24,
            borderTop: '1px solid #f0f0f0',
            paddingTop: 16,
            background: '#fff',
            zIndex: 10,
          }}
        >
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                <Button onClick={handleClose}>Cancel</Button>
                {step > BILLING_STEPS.SEARCH_CUSTOMER.value && (
                  <Button onClick={handleBack}>Back</Button>
                )}
              </Space>
            </Col>
            <Col>
              
              {step === BILLING_STEPS.SELECT_SCHEME.value && (
                <Button
                  type="primary"
                  onClick={handleProceedAccrued}
                  style={{ width: 180 }}
                  aria-label="Proceed to Billing"
                >
                  Proceed to Billing
                </Button>
              )}
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
};

export default CreateBillPopup;
